<template>
  <div ref="editorContainer" class="w-full h-full" />
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, watch, computed } from 'vue'
import * as monaco from 'monaco-editor'
import { useEditorStore } from '@/stores/editorStore'
import { configureMonaco, DEFAULT_OPTIONS } from './monacoConfig'

const props = defineProps<{ columnId: string }>()

const editorContainer = ref<HTMLElement>()
const editorStore     = useEditorStore()

let editor: monaco.editor.IStandaloneCodeEditor

const column = computed(() =>
  editorStore.columns.find(c => c.id === props.columnId)
)

onMounted(() => {
  configureMonaco()

  editor = monaco.editor.create(editorContainer.value!, {
    value:    '',
    language: 'plaintext',
    ...DEFAULT_OPTIONS,
  })

  editorStore.setEditorInstance(props.columnId, editor)

  // Focus → make this the active column
  editor.onDidFocusEditorText(() => {
    editorStore.setActiveColumn(props.columnId)
  })

  // Cmd+S / Ctrl+S → format then save for this column
  editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS, async () => {
    try {
      await editorStore.formatCurrentFile(props.columnId)
    } catch {
      // formatting failed — save anyway
    }
    await editorStore.saveCurrentFile(editor.getValue(), props.columnId)
  })

  // Alt+Shift+F → format document
  editor.addCommand(monaco.KeyMod.Alt | monaco.KeyMod.Shift | monaco.KeyCode.KeyF, async () => {
    try {
      await editorStore.formatCurrentFile(props.columnId)
    } catch { /* unsupported file type */ }
  })

  // Propagate edits to the dirty flag in the store
  editor.onDidChangeModelContent(() => {
    const col = editorStore.columns.find(c => c.id === props.columnId)
    if (col?.activeFile) {
      editorStore.markDirty(col.activeFile.path)
    }
  })

  // Load the file that is already active in this column on first mount
  const activeFile = column.value?.activeFile
  if (activeFile) {
    const model = editorStore.getModel(activeFile.path)
    if (model) {
      const initialModel = editor.getModel()
      editor.setModel(model)
      initialModel?.dispose()
    }
  }
})

// Switch the editor to the model of the newly active tab in this column
watch(
  () => column.value?.activeFile,
  (file) => {
    if (!file || !editor) return
    const model = editorStore.getModel(file.path)
    if (model && editor.getModel() !== model) {
      editor.setModel(model)
    }
  },
)

onUnmounted(() => {
  editor?.dispose()
})
</script>
